import { defineStore } from "pinia";
import { reactive, computed } from "vue";
import debounce from "lodash.debounce";
import ApiService from "~/services/ApiService";

export const useCasinoGamesStore = defineStore("casino-games", () => {
	const state = reactive({
		games: [],
		currentPage: 1,
		totalPages: 1,
		showingItems: 0,
		totalItems: 0,
		searchByProvider: [],
		providersSelected: [],
		providers: [],
		searchString: "",
		category: [],
		isLoading: true,
		categoryId: null,
		categorySlug: null,
		categoryName: null,
	});

	const games = computed(() => state.games);

	const gameCountPercentage = computed(() =>
		Math.round((state.showingItems / state.totalItems) * 100),
	);

	const gameProviders = computed(() => state.providers);

	const get = async () => {
		try {
			state.isLoading = true;
			let getGamesUrl = `/v2/casino-games?page=${state.currentPage}`;

			if (state.searchByProvider.length > 0) {
				getGamesUrl += `&casino_provider_id[]=${state.searchByProvider.join("&casino_provider_id[]=")}`;
			}

			if (state.searchString && state.searchString !== "") {
				getGamesUrl += `&search=${state.searchString}`;
			}

			if (state.categoryId) {
				getGamesUrl += `&casino_game_category_ids[]=${state.categoryId}`;
			}

			getGamesUrl += "&order=clicks";

			ApiService.get(getGamesUrl)
				.then((res) => {
					state.games = [...state.games, ...res.data.data];
					state.currentPage = res.data.meta.current_page;
					state.totalPages = res.data.meta.last_page;
					state.showingItems = res.data.meta.to;
					state.totalItems = res.data.meta.total;
					state.isLoading = false;
				})
				.catch((error) => {
					console.log(error);
					state.isLoading = false;
				});
		} catch (error) {
			console.log("We were unable to fetch the casino games.");
			console.log(error);
			state.isLoading = false;
		}
	};
	const nextPage = async () => {
		if (state.currentPage < state.totalPages) {
			state.currentPage++;
			await get();
		}
	};

	async function getProvider(param) {
		if (param === null || param === undefined) {
			return;
		}

		const categoryParam = param ? `casino_game_category_ids[]=${param}` : "";

		try {
			const response = await ApiService.get(
				`/api/casino-games/providers?${categoryParam}`,
			);
			state.providers = response.data;
		} catch (error) {
			console.log("We were unable to fetch the casino providers.");
			console.error(error);
		}
	}

	const searchByText = debounce(() => {
		get();
		state.currentPage = 1;
		state.games = [];
	}, 500);

	const toggleProvider = (providerId) => {
		if (state.searchByProvider.includes(providerId)) {
			state.searchByProvider = state.searchByProvider.filter(
				(id) => id !== providerId,
			);
		} else {
			state.searchByProvider.push(providerId);
		}

		state.currentPage = 1;
		state.games = [];
		get();
	};

	const toggleProviderName = (provider) => {
		if (state.providersSelected.includes(provider)) {
			state.providersSelected = state.providersSelected.filter(
				(id) => id !== provider,
			);
		} else {
			state.providersSelected.push(provider);
		}
	};

	const resetFilters = () => {
		state.searchByProvider = [];
		state.providersSelected = [];
		state.searchString = "";
		state.currentPage = 1;
		state.games = [];
		state.providersSelected = [];
	};

	const clearGames = () => {
		state.games = [];
		state.isLoading = true;
	};
	const getLive = async () => {
		resetFilters();
		state.categoryId = 2;
		state.categorySlug = "live";
		state.categoryName = "Live";
		await get();
	};

	const getSlots = async () => {
		resetFilters();
		state.categoryId = 1;
		state.categorySlug = "slots";
		state.categoryName = "Slots";
		await get();
	};

	const getAllCasinoGames = async () => {
		try {
			state.isLoading = true;
			const gamesResponse = await ApiService.get(
				"/v2/casino-games?&order=clicks",
			);
			state.games = gamesResponse.data.data;
			state.currentPage = gamesResponse.data.meta.current_page;
			state.totalPages = gamesResponse.data.meta.last_page;
			state.showingItems = gamesResponse.data.meta.to;
			state.totalItems = gamesResponse.data.meta.total;
			state.isLoading = false;
		} catch (error) {
			console.log("We were unable to fetch the casino games.");
			console.log(error);
			state.isLoading = false;
		}
	};
	const getCategoryBySlug = async (categorySlug) => {
		try {
			state.isLoading = true;
			const categoryResponse = await ApiService.get(
				"/v2/casino-games/category",
			);
			const category = categoryResponse.data.find(
				(cat) => cat.slug === categorySlug,
			);
			if (category) {
				state.categoryId = category.id;
				state.categorySlug = category.slug;
				state.categoryName = category.name;
				const getGamesUrl = `/v2/casino-games?page=1&casino_game_category_ids[]=${state.categoryId}&order=clicks`;

				const gamesResponse = await ApiService.get(getGamesUrl);
				state.games = gamesResponse.data.data;
				state.currentPage = gamesResponse.data.meta.current_page;
				state.totalPages = gamesResponse.data.meta.last_page;
				state.showingItems = gamesResponse.data.meta.to;
				state.totalItems = gamesResponse.data.meta.total;
			} else {
				state.games = [];
				state.categoryId = null;
				state.categorySlug = null;
				state.categoryName = null;
				console.error("No category found matching slug:", categorySlug);
			}
		} catch (error) {
			console.error("Failed to fetch category by slug", error);
			state.isLoading = false;
		} finally {
			state.isLoading = false;
		}
	};

	return {
		state,
		games,
		gameCountPercentage,
		gameProviders,
		get,
		nextPage,
		toggleProvider,
		resetFilters,
		getLive,
		getSlots,
		searchByText,
		getProvider,
		toggleProviderName,
		getCategoryBySlug,
		getAllCasinoGames,
		clearGames,
	};
});
